import { Injectable } from '@angular/core';
import { BillingService, ProductPricing } from '@galaxy/billing';
import { App, AppKey, AppPartnerService } from '@galaxy/marketplace-apps';
import { TranslateService } from '@ngx-translate/core';
import { Package, PricingDisplayOption } from '@vendasta/marketplace-packages';
import {
  CommonField,
  ConfigInterface,
  CustomField,
  CustomerSalesOrdersService,
  Field,
  FieldInterface,
  LineItem,
  LineItemInterface,
  Order,
  RevenueComponent,
  RevenuePeriod,
  SalesOrdersService,
  Status,
  UserInterface,
} from '@vendasta/sales-orders';
import { SalesOrderStoreService } from '@vendasta/sales-ui';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  shareReplay,
  startWith,
  switchMap,
  switchMapTo,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AccountGroup, AccountGroupService } from '../account-group';
import { PackageService } from '../core/package.service';
import { getTotalPriceForLineItems } from './confirmation/util';
import { addPricingToLineItems, getPackagePricingFromLineItem, packagesToLineItems } from './conversion-utils';

@Injectable()
export class OrderService {
  static readonly fileUploadUrl = '/ajax/v1/marketplace/file/upload/';

  private orderId$$: BehaviorSubject<string> = new BehaviorSubject(null);
  private specifiedLineItems$$: BehaviorSubject<LineItemInterface[]> = new BehaviorSubject(null);
  private readonly orderPermissionDenied$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly orderId$: Observable<string>;
  readonly specifiedLineItems$: Observable<LineItemInterface[]>;
  readonly order$: Observable<Order | null>;
  readonly readOnly$: Observable<boolean>;
  readonly orderPermissionDenied$: Observable<boolean> = this.orderPermissionDenied$$.asObservable();
  readonly allAppsInTheOrder$: Observable<App[]>;
  readonly lineItems$: Observable<LineItem[]>;
  readonly orderTotal$: Observable<number>;
  readonly cannotBIYPackageWarning$: Observable<string>;
  readonly commonFormSectionsSaved$$: Subject<void> = new Subject<void>(); // used to refresh saved order form values.
  readonly customFieldsSaved$$: Subject<void> = new Subject<void>(); // used to refresh saved order form values.
  readonly extraFieldsSaved$$: Subject<void> = new Subject<void>(); // used to refresh saved order form values.
  readonly requiresPartnerInputSaved$$: Subject<void> = new Subject<void>(); // used to refresh saved order form values.
  readonly orderConfig$: Observable<ConfigInterface>;
  savedCommonFormFields$: Observable<CommonField[]>;
  savedCustomFields$: Observable<CustomField[]>;
  savedExtraFields$: Observable<FieldInterface[]>;
  savedRequiresPartnerInput$: Observable<boolean>;
  users$: Observable<{ [key: string]: UserInterface }>;

  constructor(
    private accountGroupService: AccountGroupService,
    private packageService: PackageService,
    private salesOrdersService: SalesOrdersService,
    private orderStoreService: SalesOrderStoreService,
    private customerSalesOrdersService: CustomerSalesOrdersService,
    private appSdk: AppPartnerService,
    private translateService: TranslateService,
    private billingService: BillingService,
  ) {
    const agid$ = this.accountGroupService.currentAccountGroupId$.pipe(
      filter((agid) => !!agid),
      distinctUntilChanged(),
    );
    this.orderId$ = this.orderId$$.pipe(
      filter((orderId) => !!orderId),
      distinctUntilChanged(),
    );

    this.specifiedLineItems$ = this.specifiedLineItems$$.pipe(
      filter((orderItems) => orderItems !== null),
      distinctUntilChanged(),
    );

    this.order$ = combineLatest([agid$, this.orderId$]).pipe(
      filter(([agid, orderId]) => !!orderId && !!agid),
      switchMap(([agid, orderId]) => {
        return this.customerSalesOrdersService.get(orderId, agid);
      }),
      tap(() => {
        this.orderPermissionDenied$$.next(false);
      }),
      catchError(() => {
        this.orderPermissionDenied$$.next(true);
        return of(null);
      }),
      startWith(null),
      publishReplay(1),
      refCount(),
    );

    this.readOnly$ = this.order$.pipe(map((order) => calculateReadOnlyOrderform(order)));

    this.users$ = this.order$.pipe(
      filter((o) => !!o),
      switchMap((o) => this.customerSalesOrdersService.getUsers(o.orderId, o.businessId)),
      startWith({}),
    );

    this.orderConfig$ = this.accountGroupService.currentAccountGroup$.pipe(
      switchMap((a) => {
        return this.salesOrdersService.getConfig(a.partnerId, a.marketId);
      }),
      publishReplay(1),
      refCount(),
    );

    const packageIds$ = combineLatest([this.specifiedLineItems$, this.order$]).pipe(
      map(([orderItems, order]) => {
        if (order) {
          return order.lineItems.filter((li) => !!li.packageId).map((li) => li.packageId);
        }
        return orderItems.map((orderItem) => orderItem.packageId).filter((id) => !!id);
      }),
    );

    const packages$: Observable<Package[]> = packageIds$.pipe(
      switchMap((packageIds) => {
        return packageIds.length > 0 ? this.packageService.getMulti(packageIds) : of([]);
      }),
      withLatestFrom(this.order$),
      map(([packages, order]) => {
        if (order) {
          packages = order.lineItems
            .map((li) => {
              const orderPackage = packages.find((x) => x.packageId === li.packageId);

              if (orderPackage) {
                orderPackage.pricing = getPackagePricingFromLineItem(li);
              }

              return orderPackage;
            })
            .filter((li) => !!li);
        }
        return packages;
      }),
      publishReplay(1),
      refCount(),
      // TODO: prevent creating order with a package in other markets
    );

    this.cannotBIYPackageWarning$ = packages$.pipe(
      map((packages) => {
        const packagesNotUsingBillingPricing = packages.filter(
          (pkg) =>
            pkg.pricingDisplayOption === PricingDisplayOption.PRICING_DISPLAY_OPTION_CONTACT_SALES ||
            pkg.pricingDisplayOption === PricingDisplayOption.PRICING_DISPLAY_OPTION_STARTING_AT,
        );
        if (packagesNotUsingBillingPricing.length > 0) {
          return this.createCannotBIYPackageWarning(packagesNotUsingBillingPricing);
        }
        return '';
      }),
    );

    this.allAppsInTheOrder$ = combineLatest([packages$, this.specifiedLineItems$]).pipe(
      map(([packages, specifiedLineItems]) => {
        let lineItems = [];
        packages.map((p) => (lineItems = lineItems.concat(p.lineItems.lineItems || [])));
        const appKeysFromPackages = lineItems.map((li) => new AppKey({ appId: li.id, editionId: li.editionId }));
        return appKeysFromPackages.concat(
          specifiedLineItems.filter((li) => !!li.appKey).map((li) => new AppKey(li.appKey)),
        );
      }),
      withLatestFrom(this.accountGroupService.currentAccountGroup$),
      switchMap(([keys, accountGroup]) => {
        const filteredKeys = (keys || []).filter((k) => !!k);
        if (filteredKeys.length === 0) {
          return of([]);
        }
        return this.appSdk.getMulti(filteredKeys, accountGroup.partnerId, accountGroup.marketId, null, true);
      }),
      publishReplay(1),
      refCount(),
    );

    const billingRetailPrices$: Observable<{ key: AppKey; pricing: ProductPricing }[]> = combineLatest([
      this.specifiedLineItems$,
      this.accountGroupService.currentAccountGroup$,
      this.allAppsInTheOrder$,
    ]).pipe(
      switchMap(([specifiedLineItems, accountGroup, apps]: [LineItemInterface[], AccountGroup, App[]]) => {
        const specifiedApps: LineItemInterface[] = specifiedLineItems.filter((l) => !!l.appKey);
        if (specifiedApps.length === 0) {
          return of([]);
        }
        const appKeysBySku: { [sku: string]: AppKey } = getAppKeysBySkus(specifiedApps, apps);
        return this.billingService
          .getMultiRetailPricing(accountGroup.partnerId, null, Object.keys(appKeysBySku), accountGroup.marketId)
          .pipe(
            map((pricings) => {
              return Object.keys(pricings).map((sku) => {
                const productPricing: ProductPricing = pricings[sku];
                const appKey: AppKey = appKeysBySku[sku];
                return { key: appKey, pricing: productPricing };
              });
            }),
          );
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.lineItems$ = combineLatest([this.order$, billingRetailPrices$, this.specifiedLineItems$, packages$]).pipe(
      map(([order, prices, specifiedLineItems, packages]) => {
        if (order) {
          return order.lineItems;
        }
        const orderConfItems = packagesToLineItems(
          packages.map((pkg) => ({
            package: pkg,
            lineItem: specifiedLineItems.find((lineItem) => !!lineItem && lineItem.packageId === pkg.packageId),
          })),
        );
        const appItems = addPricingToLineItems(
          specifiedLineItems.filter((li) => !!li.appKey),
          prices,
        );
        const concatOrderConfItems = orderConfItems.concat(appItems);
        concatOrderConfItems.forEach((li) => {
          li.currentRevenue.revenueComponents.sort(sortRevenuesByFrequency);
        });
        return concatOrderConfItems;
      }),
      publishReplay(1),
      refCount(),
    );

    this.orderTotal$ = this.lineItems$.pipe(map((lineItems) => getTotalPriceForLineItems(lineItems)));

    this.savedCommonFormFields$ = this.commonFormSectionsSaved$$.pipe(
      startWith(undefined as void), // ensure we get the initial value
      switchMapTo(agid$),
      map((accountGroupId) => {
        const key = buildCommonFieldsKey(accountGroupId);
        const orderFormJson = window.sessionStorage.getItem(key);
        if (orderFormJson) {
          return JSON.parse(orderFormJson);
        }
      }),
    );
    this.savedCustomFields$ = this.customFieldsSaved$$.pipe(
      startWith(undefined as void), // ensure we get the initial value
      switchMapTo(agid$),
      map((accountGroupId) => {
        const key = buildCustomFieldsKey(accountGroupId);
        const orderFormJson = window.sessionStorage.getItem(key);
        let jsonFields: CustomField[];
        if (orderFormJson) {
          jsonFields = JSON.parse(orderFormJson);
        }

        return jsonFields
          ? jsonFields.map((customField) => {
              const returnField = new CustomField(customField);
              returnField.fields = returnField.fields.map((field) => {
                return new Field(field);
              });
              return returnField;
            })
          : [];
      }),
    );
    this.savedExtraFields$ = this.extraFieldsSaved$$.pipe(
      startWith(undefined as void),
      switchMapTo(agid$),
      map((accountGroupId) => {
        const key = buildExtraFieldsKey(accountGroupId);
        const orderFormJson = window.sessionStorage.getItem(key);
        if (orderFormJson) {
          return JSON.parse(orderFormJson);
        } else {
          return [];
        }
      }),
    );

    this.savedRequiresPartnerInput$ = this.requiresPartnerInputSaved$$.pipe(
      startWith(undefined as void),
      switchMapTo(agid$),
      map((accountGroupId) => {
        const key = buildRequiresPartnerInputKey(accountGroupId);
        const requiresPartnerInputJson = window.sessionStorage.getItem(key);
        if (requiresPartnerInputJson) {
          return JSON.parse(requiresPartnerInputJson);
        } else {
          return false;
        }
      }),
    );
  }

  // Different products can have order form fields with the same fieldID, which will cause problem.
  // Please refer to https://github.com/vendasta/business-center-client/pull/1002
  // We will prefix the fieldID with uuid, and take it off before we same the order form into session storage
  static decodeCustomFieldId(fieldId: string): string {
    return fieldId.replace(new RegExp('(.*#)'), '');
  }

  private createCannotBIYPackageWarning(packages: Package[]): string {
    const namesOfPackages = packages.map((pkg) => pkg.name);
    return this.translateService.instant('STORE.ORDER_CONFIRMATION.CANNOT_BIY_PACKAGE_WARNING', {
      package_names: namesOfPackages.join('\n    \u2022 '),
    });
  }

  initialize(orderId: string, specifiedLineItems: LineItemInterface[]): void {
    this.orderId$$.next(orderId);
    this.specifiedLineItems$$.next(specifiedLineItems);
  }

  saveCommonFields(commonFields: CommonField[], accountGroupId: string): void {
    const key = buildCommonFieldsKey(accountGroupId);
    const orderFormJson = JSON.stringify(commonFields);
    window.sessionStorage.setItem(key, orderFormJson);
    this.commonFormSectionsSaved$$.next();
  }

  saveCustomFields(fields: CustomField[], accountGroupId: string): void {
    const key = buildCustomFieldsKey(accountGroupId);
    const orderFormJson = JSON.stringify(fields);
    window.sessionStorage.setItem(key, orderFormJson);
    this.customFieldsSaved$$.next();
  }

  saveExtraFields(fields: FieldInterface[], accountGroupId: string): void {
    const key = buildExtraFieldsKey(accountGroupId);
    const orderFormJson = JSON.stringify(fields);
    window.sessionStorage.setItem(key, orderFormJson);
    this.extraFieldsSaved$$.next();
  }

  saveRequiresPartnerInput(requiresPartnerInput: boolean, accountGroupId: string): void {
    const key = buildRequiresPartnerInputKey(accountGroupId);
    const requiresPartnerInputJson = JSON.stringify(requiresPartnerInput);
    window.sessionStorage.setItem(key, requiresPartnerInputJson);
    this.requiresPartnerInputSaved$$.next();
  }

  refreshOrder(orderId: string, accountGroupId: string): void {
    this.orderId$$.next(orderId);
    this.orderStoreService.refreshOrder(orderId, accountGroupId);
  }
}

function buildCustomFieldsKey(accountGroupId: string): string {
  return `custom-${accountGroupId}`;
}

function buildCommonFieldsKey(accountGroupId: string): string {
  return `common-${accountGroupId}`;
}

function buildExtraFieldsKey(accountGroupId: string): string {
  return `extra-${accountGroupId}`;
}

function buildRequiresPartnerInputKey(accountGroupId: string): string {
  return `req-partner-input-${accountGroupId}`;
}

function getAppKeysBySkus(specifiedApps: LineItemInterface[], apps: App[]): { [sku: string]: AppKey } {
  const appKeysBySku: { [sku: string]: AppKey } = {};
  specifiedApps.forEach((specifiedApp) => {
    const app: App = apps.find((a) => a.key.appId === specifiedApp.appKey.appId);
    if (specifiedApp.appKey.editionId && app?.editionInformation?.editions?.length > 0) {
      const edition = app.editionInformation.editions.find((e) => e.appKey.editionId === specifiedApp.appKey.editionId);
      if (edition?.billingId) {
        appKeysBySku[edition.billingId] = new AppKey(specifiedApp.appKey);
      }
    } else {
      appKeysBySku[app.externalIdentifiers.billingId] = new AppKey(specifiedApp.appKey);
    }
  });
  return appKeysBySku;
}

// This function is used for sorting revenues by frequency into the order: Monthly, Yearly, Once
export function sortRevenuesByFrequency(revenueA: RevenueComponent, revenueB: RevenueComponent): number {
  const revenueFrequencyComparisonMap: Map<RevenuePeriod, Map<RevenuePeriod, number>> = new Map<
    RevenuePeriod,
    Map<RevenuePeriod, number>
  >([
    [
      RevenuePeriod.MONTHLY,
      new Map([
        [RevenuePeriod.MONTHLY, 0],
        [RevenuePeriod.YEARLY, -1],
        [RevenuePeriod.ONETIME, -1],
      ]),
    ],
    [
      RevenuePeriod.YEARLY,
      new Map([
        [RevenuePeriod.MONTHLY, 1],
        [RevenuePeriod.YEARLY, 0],
        [RevenuePeriod.ONETIME, -1],
      ]),
    ],
    [
      RevenuePeriod.ONETIME,
      new Map([
        [RevenuePeriod.MONTHLY, 1],
        [RevenuePeriod.YEARLY, 1],
        [RevenuePeriod.ONETIME, 0],
      ]),
    ],
  ]);

  if (revenueFrequencyComparisonMap.get(revenueA.period) === undefined) {
    return 0;
  }

  const compareVal = revenueFrequencyComparisonMap.get(revenueA.period).get(revenueB.period);
  return compareVal ? compareVal : 0;
}

// The specifiedAppKeys can have an empty edition. If this is the case and the actual app is our O&O then the default edition id is used
export function findAppFromOrderItems(specifiedAppKeys: AppKey[], apps: App[]): App[] {
  return specifiedAppKeys
    .map((key) => {
      let app = apps.find((a) => a.key.appId === key.appId && a.key.editionId === key.editionId);
      if (!app && key.editionId === '') {
        app = apps.find((a) => a.key.appId === key.appId);
      }
      return app;
    })
    .filter((a) => !!a);
}

// an order can only be edited by an smb user if the status set to waiting for their approval
export function calculateReadOnlyOrderform(o: Order): boolean {
  let readOnly = false;
  if (o) {
    readOnly = o.status !== Status.SUBMITTED_FOR_CUSTOMER_APPROVAL;
  }
  return readOnly;
}
